// Rain presentation, "mood over physics" (DESIGN.md §5): an overcast sky and
// murky haze, a wet-road look on the track, falling rain streaks around the
// camera and tyre spray from the player's car. Cheap on phone GPUs:
//
// - Mood: the existing sky dome, clouds, sun, lights, fog and exposure are
//   blended towards an overcast palette (found by name; no other code changes).
//   Values someone else changes later (e.g. fog distances on a quality change)
//   are picked up as the new dry baseline.
// - Wet track: each track material gets a wet twin (shares the textures), made
//   and shader-compiled up front, and swapped in while the track is wet: darker
//   albedo, glossier specular, and a fresnel "sky reflection" added at grazing
//   angles. The rubbered racing line reflects less as it dries (dryLine). At
//   wetness 0 the original materials are back on the meshes, untouched.
// - Rain: one draw call of camera-relative streak quads (custom shader), wrapped
//   in a box around the camera and stretched along the drops' velocity relative
//   to the car. Depth-tested, so bodywork hides drops behind it.
// - Spray: one draw call of soft billboards in the car's frame, thrown up and
//   back from the tyres; grows with speed × wetness.
//
// Nothing here allocates per frame.

import {
  Color3,
  FresnelParameters,
  Mesh,
  Scene,
  ShaderMaterial,
  ShaderStore,
  StandardMaterial,
  Vector3,
  VertexData,
} from './babylon';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Material } from '@babylonjs/core/Materials/material';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import type { Light } from '@babylonjs/core/Lights/light';
import { carParams } from '../sim/car';

export type WeatherQuality = 'low' | 'medium' | 'high';

export interface WeatherState {
  /** Current rain intensity, 0..1. */
  rain: number;
  /** Standing water on the track, 0..1. */
  wetness: number;
  /** How far a dry racing line has formed, 0..1. */
  dryLine?: number;
  carX: number;
  carZ: number;
  /** Ground height under the car. */
  carY: number;
  heading: number;
  /** m/s */
  speed: number;
  /** Frame time, s. */
  dt: number;
}

export interface WeatherVisuals {
  update(state: WeatherState): void;
  dispose(): void;
  /** For tests and the dev overlay: what the last update drew. */
  readonly stats: { drops: number; sprayParticles: number; wetMaterials: number };
}

export interface WeatherOptions {
  quality: () => WeatherQuality;
}

// ---------------------------------------------------------------------------
// Tuning

/** Overcast palettes: light (drizzle, lingering cloud) and storm (heavy rain). */
const OVERCAST = {
  lightHorizon: new Color3(0.7, 0.71, 0.72),
  lightZenith: new Color3(0.52, 0.56, 0.62),
  stormHorizon: new Color3(0.45, 0.47, 0.5),
  stormZenith: new Color3(0.28, 0.3, 0.34),
  hemiDiffuse: new Color3(0.84, 0.89, 0.98),
  hemiGround: new Color3(0.27, 0.29, 0.27),
};

/** Heavy-rain haze: fog closes in to these distances (m). */
const RAIN_FOG = { start: 12, end: 520 };

/** Max streaks per quality level (drawn = max × rain). */
const DROPS: Record<WeatherQuality, number> = { high: 5200, medium: 3400, low: 1800 };
/** Spray particles: per front wheel (always), per rear wheel by quality. */
const SPRAY_FRONT = 40;
const SPRAY_REAR: Record<WeatherQuality, number> = { high: 22, medium: 12, low: 0 };
const SPRAY_REAR_MAX = 22;

/** Rain box around the camera (m) and how far its centre sits ahead of the camera. */
const BOX = new Vector3(30, 16, 30);
const BOX_AHEAD = 8;
const FALL_SPEED = 8.5;
const WIND = new Vector3(1.6, 0, 0.6);

interface WetStyle {
  /** Albedo multiplier when fully wet. */
  darken: number;
  specular: number;
  specularPower: number;
  /** Strength of the fresnel sky reflection (0 = none). */
  sheen: number;
  /** Use the road's rubber overlay for the drying line. */
  rubberLine?: boolean;
}

/** Track meshes (built by trackMesh.ts as `track-<layer>`) and how each looks wet. */
const WET_STYLES: Record<string, WetStyle> = {
  'track-road': { darken: 0.38, specular: 0.55, specularPower: 110, sheen: 0.7, rubberLine: true },
  'track-runoffAsphalt': { darken: 0.42, specular: 0.45, specularPower: 90, sheen: 0.6 },
  'track-kerbs': { darken: 0.72, specular: 0.5, specularPower: 90, sheen: 0.4 },
  'track-lines': { darken: 0.78, specular: 0.4, specularPower: 70, sheen: 0.35 },
  'track-gravel': { darken: 0.68, specular: 0.06, specularPower: 24, sheen: 0 },
  'track-grass': { darken: 0.82, specular: 0.05, specularPower: 16, sheen: 0 },
};

/**
 * Fresnel term = pow(bias + |cos(view, normal)|, power), clamped; the sheen is
 * (1 − term). bias 0 / power 0.4 follows water's Schlick curve closely from 10
 * to 60 m ahead of a 1.2 m eye (≈0.55 → 0.8); it's too strong only right in
 * front of the car, which the nose and tyres mostly hide.
 */
const SHEEN_BIAS = 0;
const SHEEN_POWER = 0.4;
/** Rubber overlay value on the racing line centre (see rubberTexture in trackMesh.ts). */
const RUBBER_LINE = 0.74;
/** Largest sheen reduction on the line at dryLine 1 (see applyWet). */
const RUBBER_MAX_DRY = 0.45;

// ---------------------------------------------------------------------------
// Small helpers

const clamp01 = (v: number): number => (v <= 0 ? 0 : v >= 1 ? 1 : v);
const lerp = (a: number, b: number, t: number): number => (t === 0 ? a : a + (b - a) * t);
const smooth = (t: number): number => t * t * (3 - 2 * t);
const finite = (v: number | undefined, fallback = 0): number => (v !== undefined && Number.isFinite(v) ? v : fallback);

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * A number owned by someone else that weather nudges. The current value is the
 * baseline unless it is still what weather last wrote; at blend 0 the baseline
 * is written back exactly.
 */
class NumKnob {
  private base = 0;
  private last: number | undefined;
  constructor(
    private readonly get: () => number,
    private readonly set: (v: number) => void,
  ) {}
  apply(f: (base: number) => number): void {
    const cur = this.get();
    if (this.last === undefined || cur !== this.last) this.base = cur;
    const v = f(this.base);
    if (v !== cur) this.set(v);
    this.last = v;
  }
  restore(): void {
    if (this.last !== undefined && this.get() === this.last && this.base !== this.last) this.set(this.base);
    this.last = undefined;
  }
}

/** Same as NumKnob for a colour object (edited in place). */
class ColorKnob {
  private readonly base = new Color3();
  private readonly last = new Color3();
  private has = false;
  private readonly out = new Color3();
  constructor(private readonly get: () => Rgb | undefined) {}
  apply(f: (base: Color3, out: Color3) => void): void {
    const c = this.get();
    if (!c) return;
    if (!this.has || c.r !== this.last.r || c.g !== this.last.g || c.b !== this.last.b) this.base.set(c.r, c.g, c.b);
    f(this.base, this.out);
    c.r = this.out.r;
    c.g = this.out.g;
    c.b = this.out.b;
    this.last.copyFrom(this.out);
    this.has = true;
  }
  restore(): void {
    const c = this.get();
    if (c && this.has && c.r === this.last.r && c.g === this.last.g && c.b === this.last.b) {
      c.r = this.base.r;
      c.g = this.base.g;
      c.b = this.base.b;
    }
    this.has = false;
  }
}

function lerpColor(a: Rgb, b: Rgb, t: number, out: Color3): Color3 {
  if (t === 0) return out.set(a.r, a.g, a.b);
  return out.set(a.r + (b.r - a.r) * t, a.g + (b.g - a.g) * t, a.b + (b.b - a.b) * t);
}

/** Deterministic PRNG (mulberry32) so the rain looks the same every load. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Shaders

ShaderStore.ShadersStore['foneRainVertexShader'] = `
precision highp float;
attribute vec3 position; // per-drop seed in [0,1)^3
attribute vec2 uv;       // x: side (-1/1), y: 0 head / 1 tail
attribute vec2 uv2;      // per-drop randoms
uniform mat4 viewProjection;
uniform mat4 projection;
uniform vec3 uCam;
uniform vec3 uCenterRel; // box centre relative to the camera
uniform vec3 uOffset;    // world drift of the drops minus box centre, wrapped to the box
uniform vec3 uBox;
uniform vec3 uVel;       // drop velocity relative to the camera, m/s
uniform float uExposure;
uniform vec2 uScreen;
uniform float uAlpha;
varying float vAlpha;
varying float vAlong;
void main() {
  vec3 local = mod(position * uBox + uOffset, uBox) - 0.5 * uBox;
  vec3 head = uCenterRel + local;
  vec3 tail = head - uVel * uExposure * (0.7 + 0.6 * uv2.x);
  vec4 ch = viewProjection * vec4(uCam + head, 1.0);
  vec4 ct = viewProjection * vec4(uCam + tail, 1.0);
  float r = length(local.xz) / (0.5 * uBox.x);
  float fade = smoothstep(0.7, 1.8, length(head))
    * (1.0 - smoothstep(0.55, 1.0, r))
    * (1.0 - smoothstep(0.3, 0.5, abs(local.y) / uBox.y));
  if (ch.w < 0.25 || ct.w < 0.25) fade = 0.0;
  vec2 sh = ch.xy / ch.w * 0.5 * uScreen;
  vec2 st = ct.xy / ct.w * 0.5 * uScreen;
  vec2 d = st - sh;
  float len = length(d);
  vec2 dir = len > 0.001 ? d / len : vec2(0.0, 1.0);
  vec4 c = uv.y < 0.5 ? ch : ct;
  if (uv.y > 0.5 && len < 3.0) {
    // Keep a visible minimum streak length (slow drops far away).
    c = vec4((sh + dir * 3.0) / (0.5 * uScreen) * ch.w, ch.z, ch.w);
  }
  // 9 mm wide drops in screen pixels; thinner than a pixel fades instead.
  float wpx = 0.009 * projection[1][1] * 0.5 * uScreen.y / max(c.w, 0.25);
  float cover = clamp(wpx, 0.15, 1.0);
  wpx = clamp(wpx, 1.0, 5.0);
  vec2 perp = vec2(-dir.y, dir.x);
  c.xy += perp * uv.x * (wpx / uScreen) * c.w;
  vAlpha = uAlpha * fade * cover * (0.45 + 0.55 * uv2.y);
  vAlong = uv.y;
  gl_Position = c;
}
`;

ShaderStore.ShadersStore['foneRainFragmentShader'] = `
precision mediump float;
uniform vec3 uColor;
varying float vAlpha;
varying float vAlong;
void main() {
  gl_FragColor = vec4(uColor, vAlpha * mix(1.0, 0.2, vAlong));
}
`;

ShaderStore.ShadersStore['foneSprayVertexShader'] = `
precision highp float;
attribute vec3 position; // emitter in the car frame (tyre contact patch, rear side)
attribute vec2 uv;       // quad corner, -1..1
attribute vec4 color;    // x phase, y lifetime (s), z 0 front / 1 rear, w random
attribute vec3 normal;   // randoms in -1..1
uniform mat4 world;
uniform mat4 viewProjection;
uniform vec3 uRight;
uniform vec3 uUp;
uniform vec3 uCam;
uniform float uTime;
uniform float uSpeed;
uniform float uAmount;
varying vec2 vUv;
varying float vAlpha;
varying float vRnd;
void main() {
  float life = color.y;
  float rear = color.z;
  float a = fract(uTime / life + color.x);
  float t = a * life;
  float s = uSpeed;
  float side = position.x < 0.0 ? -1.0 : 1.0;
  // Water leaves the back of the tyre going up at about road speed; air drag
  // stops the climb and sweeps it back relative to the car.
  float tauUp = mix(0.04, 0.07, rear);
  float y = min(mix(1.8, 3.4, rear), s * mix(1.1, 0.9, rear) * (0.55 + 0.45 * normal.y) * tauUp) * (1.0 - exp(-t / tauUp));
  float tauB = mix(0.09, 0.14, rear);
  float back = s * (0.75 + 0.2 * normal.z) * (t - tauB * (1.0 - exp(-t / tauB)));
  float outward = side * (0.06 + abs(normal.x) * mix(0.35, 0.8, rear)) * t * (1.0 + s * 0.05);
  vec3 local = position + vec3(outward, y, -back);
  vec3 wp = (world * vec4(local, 1.0)).xyz;
  float size = mix(0.25, 0.5, rear) + t * mix(4.0, 5.5, rear);
  vec3 p = wp + (uRight * uv.x + uUp * uv.y) * 0.5 * size;
  float near = smoothstep(1.0, 2.6, length(wp - uCam));
  float env = smoothstep(0.0, 0.15, a) * (1.0 - a) * (1.0 - a);
  // Big puffs thin out (and near ones fade) so they read as mist, not orbs.
  vAlpha = uAmount * env * near * mix(1.3, 1.2, rear) * (0.65 + 0.35 * color.w) / (1.0 + 0.8 * size);
  vUv = uv;
  vRnd = color.w;
  gl_Position = viewProjection * vec4(p, 1.0);
}
`;

ShaderStore.ShadersStore['foneSprayFragmentShader'] = `
precision mediump float;
uniform vec3 uColor;
varying vec2 vUv;
varying float vAlpha;
varying float vRnd;
void main() {
  float r = length(vUv);
  // Soft gaussian-ish puff that reaches zero at the quad edge; a slight lopsided
  // density per particle keeps overlapping puffs from looking like bubbles.
  float edge = max(0.0, 1.0 - r * r);
  float blob = exp(-3.0 * r * r) * edge * (0.85 + 0.15 * (vUv.x * (vRnd - 0.5) + vUv.y * 0.5));
  gl_FragColor = vec4(uColor, vAlpha * blob);
}
`;

// ---------------------------------------------------------------------------
// Rain and spray meshes

function buildRainMesh(scene: Scene, maxDrops: number): Mesh {
  const rand = rng(0x5eba9);
  const positions = new Float32Array(maxDrops * 12);
  const uvs = new Float32Array(maxDrops * 8);
  const uvs2 = new Float32Array(maxDrops * 8);
  const indices = new Uint16Array(maxDrops * 6);
  const corners = [-1, 0, 1, 0, 1, 1, -1, 1];
  for (let i = 0; i < maxDrops; i++) {
    const sx = rand();
    const sy = rand();
    const sz = rand();
    const r1 = rand();
    const r2 = rand();
    for (let k = 0; k < 4; k++) {
      const v = i * 4 + k;
      positions.set([sx, sy, sz], v * 3);
      uvs[v * 2] = corners[k * 2]!;
      uvs[v * 2 + 1] = corners[k * 2 + 1]!;
      uvs2[v * 2] = r1;
      uvs2[v * 2 + 1] = r2;
    }
    indices.set([i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3], i * 6);
  }
  const mesh = new Mesh('weather-rain', scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.uvs2 = uvs2;
  vd.indices = indices;
  vd.applyToMesh(mesh, false);
  return mesh;
}

function buildSprayMesh(scene: Scene): { mesh: Mesh; frontCount: number } {
  const L = carParams.wheelbase.value;
  const rearAxle = -L * carParams.frontWeight.value;
  const frontAxle = rearAxle + L;
  const rand = rng(0x5b7a1);
  type P = { x: number; z: number; rear: number };
  const parts: P[] = [];
  // Front wheels first (interleaved L/R), then rear, so drawing a prefix keeps both sides.
  for (let i = 0; i < SPRAY_FRONT * 2; i++) parts.push({ x: i % 2 ? 0.8 : -0.8, z: frontAxle - 0.3, rear: 0 });
  for (let i = 0; i < SPRAY_REAR_MAX * 2; i++) parts.push({ x: i % 2 ? 0.78 : -0.78, z: rearAxle - 0.35, rear: 1 });
  const n = parts.length;
  const positions = new Float32Array(n * 12);
  const uvs = new Float32Array(n * 8);
  const colors = new Float32Array(n * 16);
  const normals = new Float32Array(n * 12);
  const indices = new Uint16Array(n * 6);
  const corners = [-1, -1, 1, -1, 1, 1, -1, 1];
  parts.forEach((p, i) => {
    const across = (rand() - 0.5) * (p.rear ? 0.3 : 0.22);
    const life = p.rear ? 0.32 + rand() * 0.16 : 0.22 + rand() * 0.12;
    const c = [rand(), life, p.rear, rand()];
    const nr = [rand() * 2 - 1, rand() * 2 - 1, rand() * 2 - 1];
    for (let k = 0; k < 4; k++) {
      const v = i * 4 + k;
      positions.set([p.x + across, 0.12, p.z], v * 3);
      uvs[v * 2] = corners[k * 2]!;
      uvs[v * 2 + 1] = corners[k * 2 + 1]!;
      colors.set(c, v * 4);
      normals.set(nr, v * 3);
    }
    indices.set([i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3], i * 6);
  });
  const mesh = new Mesh('weather-spray', scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.colors = colors;
  vd.normals = normals;
  vd.indices = indices;
  vd.applyToMesh(mesh, false);
  return { mesh, frontCount: SPRAY_FRONT * 2 };
}

function shaderMat(scene: Scene, name: string, shader: string, attributes: string[], uniforms: string[]): ShaderMaterial {
  const m = new ShaderMaterial(name, scene, shader, { attributes, uniforms, needAlphaBlending: true });
  m.backFaceCulling = false;
  m.disableDepthWrite = true;
  m.fogEnabled = false;
  return m;
}

function prepareMesh(mesh: Mesh, material: Material): void {
  mesh.material = material;
  mesh.isPickable = false;
  // Vertices are placed by the shader around the camera/car: never cull.
  mesh.alwaysSelectAsActiveMesh = true;
  mesh.doNotSyncBoundingInfo = true;
  // A partial submesh (see setQuadCount) needs its own bounding info.
  mesh.subMeshes?.[0]?.setBoundingInfo(mesh.getBoundingInfo());
  mesh.setEnabled(false);
}

/** Draw only the first `count` quads of a quad mesh. */
function setQuadCount(mesh: Mesh, count: number): void {
  const sub = mesh.subMeshes?.[0];
  if (!sub) return;
  sub.indexStart = 0;
  sub.indexCount = count * 6;
  sub.verticesStart = 0;
  sub.verticesCount = count * 4;
}

// ---------------------------------------------------------------------------
// Wet track materials

const TEX_SLOTS = [
  'diffuseTexture',
  'ambientTexture',
  'opacityTexture',
  'reflectionTexture',
  'emissiveTexture',
  'specularTexture',
  'bumpTexture',
  'lightmapTexture',
  'refractionTexture',
] as const;
type TexSlot = (typeof TEX_SLOTS)[number];

interface WetEntry {
  mesh: AbstractMesh;
  orig: StandardMaterial;
  wet: StandardMaterial;
  style: WetStyle;
  rubber: boolean;
  ready: boolean;
  active: boolean;
  fresnel: FresnelParameters | undefined;
}

/** A wet twin of `orig` that shares its textures (cloning would copy canvases). */
function makeWetTwin(orig: StandardMaterial, style: WetStyle): { wet: StandardMaterial; rubber: boolean; fresnel: FresnelParameters | undefined } {
  const saved = TEX_SLOTS.map((s) => orig[s] as BaseTexture | null);
  for (const s of TEX_SLOTS) (orig as unknown as Record<TexSlot, BaseTexture | null>)[s] = null;
  let wet: StandardMaterial;
  try {
    wet = orig.clone(`${orig.name}-wet`);
  } finally {
    TEX_SLOTS.forEach((s, i) => ((orig as unknown as Record<TexSlot, BaseTexture | null>)[s] = saved[i]!));
  }
  TEX_SLOTS.forEach((s, i) => ((wet as unknown as Record<TexSlot, BaseTexture | null>)[s] = saved[i]!));
  wet.unfreeze();
  let fresnel: FresnelParameters | undefined;
  let rubber = false;
  if (style.sheen > 0) {
    // Emissive added after lighting (not tinted by the albedo), scaled by a
    // fresnel term: strongest at grazing angles, i.e. down the road.
    wet.useEmissiveAsIllumination = true;
    fresnel = new FresnelParameters({ leftColor: Color3.Black(), rightColor: Color3.Black(), bias: SHEEN_BIAS, power: SHEEN_POWER });
    wet.emissiveFresnelParameters = fresnel;
    wet.emissiveColor = Color3.White();
    if (style.rubberLine && orig.lightmapTexture && orig.lightmapTexture.level === 1) {
      // The rubber overlay (≈0.74 on the line, 1 elsewhere) drives where the line dries.
      wet.emissiveTexture = orig.lightmapTexture;
      rubber = true;
    }
  }
  return { wet, rubber, fresnel };
}

// ---------------------------------------------------------------------------

export function createWeatherVisuals(scene: Scene, camera: Camera, opts: WeatherOptions): WeatherVisuals {
  const engine = scene.getEngine();

  // ---- Rain streaks.
  const maxDrops = DROPS.high;
  const rain = buildRainMesh(scene, maxDrops);
  const rainMat = shaderMat(scene, 'weather-rain-mat', 'foneRain', ['position', 'uv', 'uv2'], [
    'viewProjection', 'projection', 'uCam', 'uCenterRel', 'uOffset', 'uBox', 'uVel', 'uExposure', 'uScreen', 'uAlpha', 'uColor',
  ]);
  prepareMesh(rain, rainMat);
  const drift = new Vector3(); // accumulated world drift of the drops, wrapped
  const rainVel = new Vector3();
  const centreRel = new Vector3();
  const offset = new Vector3();
  const screen = { x: 1, y: 1 };
  const rainColor = new Color3(0.78, 0.8, 0.84);
  let rainAlpha = 0;
  let heading = 0;
  rainMat.onBindObservable.add(() => {
    const effect = rainMat.getEffect();
    if (!effect) return;
    const cam = camera.globalPosition;
    centreRel.set(Math.sin(heading) * BOX_AHEAD, 0, Math.cos(heading) * BOX_AHEAD);
    const wrap = (v: number, b: number): number => v - Math.floor(v / b) * b;
    offset.set(
      wrap(drift.x - (cam.x + centreRel.x), BOX.x),
      wrap(drift.y - (cam.y + centreRel.y), BOX.y),
      wrap(drift.z - (cam.z + centreRel.z), BOX.z),
    );
    screen.x = engine.getRenderWidth();
    screen.y = engine.getRenderHeight();
    effect.setVector3('uCam', cam);
    effect.setVector3('uCenterRel', centreRel);
    effect.setVector3('uOffset', offset);
    effect.setVector3('uBox', BOX);
    effect.setVector3('uVel', rainVel);
    effect.setFloat('uExposure', 0.022);
    effect.setFloat2('uScreen', screen.x, screen.y);
    effect.setFloat('uAlpha', rainAlpha);
    effect.setColor3('uColor', rainColor);
  });

  // ---- Spray.
  const spray = buildSprayMesh(scene);
  const sprayMat = shaderMat(scene, 'weather-spray-mat', 'foneSpray', ['position', 'uv', 'color', 'normal'], [
    'world', 'viewProjection', 'uRight', 'uUp', 'uCam', 'uTime', 'uSpeed', 'uAmount', 'uColor',
  ]);
  prepareMesh(spray.mesh, sprayMat);
  const right = new Vector3();
  const up = new Vector3();
  const sprayColor = new Color3(0.8, 0.82, 0.85);
  let sprayTime = 0;
  let spraySpeed = 0;
  let sprayAmount = 0;
  sprayMat.onBindObservable.add(() => {
    const effect = sprayMat.getEffect();
    if (!effect) return;
    camera.getDirectionToRef(Vector3.RightReadOnly, right);
    camera.getDirectionToRef(Vector3.UpReadOnly, up);
    effect.setVector3('uRight', right);
    effect.setVector3('uUp', up);
    effect.setVector3('uCam', camera.globalPosition);
    effect.setFloat('uTime', sprayTime);
    effect.setFloat('uSpeed', spraySpeed);
    effect.setFloat('uAmount', sprayAmount);
    effect.setColor3('uColor', sprayColor);
  });

  // ---- Mood: lights, fog, exposure, sky, clouds, sun disc.
  const light = (name: string): Light | null => scene.getLightByName(name);
  const intensity = (name: string): NumKnob =>
    new NumKnob(
      () => light(name)?.intensity ?? 0,
      (v) => {
        const l = light(name);
        if (l) l.intensity = v;
      },
    );
  const sunIntensity = intensity('sun');
  const hemiIntensity = intensity('sky');
  const hemiDiffuse = new ColorKnob(() => light('sky')?.diffuse);
  const hemiGround = new ColorKnob(() => (light('sky') as (Light & { groundColor?: Color3 }) | null)?.groundColor);
  const fogStart = new NumKnob(() => scene.fogStart, (v) => (scene.fogStart = v));
  const fogEnd = new NumKnob(() => scene.fogEnd, (v) => (scene.fogEnd = v));
  const fogColor = new ColorKnob(() => scene.fogColor);
  const clearColor = new ColorKnob(() => scene.clearColor);
  const ip = scene.imageProcessingConfiguration;
  const exposure = new NumKnob(() => ip.exposure, (v) => (ip.exposure = v));
  const contrast = new NumKnob(() => ip.contrast, (v) => (ip.contrast = v));
  // Scene objects found by name (sky.ts), looked up again once a second.
  const refs = { clouds: null as StandardMaterial | null, sun: null as AbstractMesh | null, dome: null as Mesh | null };
  const resolveRefs = (): void => {
    const c = scene.getMeshByName('clouds')?.material;
    refs.clouds = c instanceof StandardMaterial ? c : null;
    refs.sun = scene.getMeshByName('sun');
    const d = scene.getMeshByName('sky');
    refs.dome = d instanceof Mesh ? d : null;
  };
  resolveRefs();
  const cloudMat = (): StandardMaterial | null => refs.clouds;
  const cloudColor = new ColorKnob(() => cloudMat()?.emissiveColor);
  // The cloud texture is added on top of the emissive colour, so dim it via its level too.
  const cloudLevel = new NumKnob(
    () => cloudMat()?.emissiveTexture?.level ?? 1,
    (v) => {
      const t = cloudMat()?.emissiveTexture;
      if (t) t.level = v;
    },
  );
  const cloudAlpha = new NumKnob(
    () => cloudMat()?.alpha ?? 1,
    (v) => {
      const m = cloudMat();
      if (m) m.alpha = v;
    },
  );
  const sunDisc = new NumKnob(
    () => refs.sun?.visibility ?? 1,
    (v) => {
      if (refs.sun) refs.sun.visibility = v;
    },
  );
  const numKnobs = [sunIntensity, hemiIntensity, fogStart, fogEnd, exposure, contrast, sunDisc, cloudLevel, cloudAlpha];
  const colorKnobs = [hemiDiffuse, hemiGround, fogColor, clearColor, cloudColor];
  let sunHidden: AbstractMesh | undefined;
  let refreezeClouds: Material | undefined;

  // Sky dome vertex colours, blended towards an overcast gradient.
  interface Dome {
    mesh: Mesh;
    orig: Float32Array;
    up: Float32Array;
    work: Float32Array;
    blend: number;
    rain: number;
  }
  let dome: Dome | undefined;
  const findDome = (): Dome | undefined => {
    const m = refs.dome;
    if (!m || m.isDisposed()) return undefined;
    if (dome?.mesh === m) return dome;
    const colors = m.getVerticesData('color');
    const pos = m.getVerticesData('position');
    if (!colors || !pos) return undefined;
    const n = pos.length / 3;
    const up = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const x = pos[i * 3]!;
      const y = pos[i * 3 + 1]!;
      const z = pos[i * 3 + 2]!;
      const len = Math.hypot(x, y, z) || 1;
      up[i] = Math.pow(Math.max(0, y / len), 0.6);
    }
    dome = { mesh: m, orig: new Float32Array(colors), up, work: new Float32Array(colors.length), blend: 0, rain: 0 };
    return dome;
  };
  const ocHorizon = new Color3();
  const ocZenith = new Color3();
  const updateDome = (blend: number, rainNow: number): void => {
    const d = findDome();
    if (!d) return;
    if (blend === 0) {
      if (d.blend !== 0) {
        d.mesh.updateVerticesData('color', d.orig);
        d.blend = 0;
      }
      return;
    }
    if (Math.abs(blend - d.blend) < 0.004 && Math.abs(rainNow - d.rain) < 0.01) return;
    if (d.blend === 0 && !d.mesh.isVerticesDataPresent('color')) return;
    // First change: make the colour buffer updatable (same values).
    if (d.blend === 0 && !d.mesh.getVertexBuffer('color')?.isUpdatable()) d.mesh.setVerticesData('color', d.orig, true, 4);
    d.blend = blend;
    d.rain = rainNow;
    const out = d.work;
    for (let i = 0; i < d.up.length; i++) {
      const t = d.up[i]!;
      const j = i * 4;
      const r = ocHorizon.r + (ocZenith.r - ocHorizon.r) * t;
      const g = ocHorizon.g + (ocZenith.g - ocHorizon.g) * t;
      const b = ocHorizon.b + (ocZenith.b - ocHorizon.b) * t;
      out[j] = d.orig[j]! + (r - d.orig[j]!) * blend;
      out[j + 1] = d.orig[j + 1]! + (g - d.orig[j + 1]!) * blend;
      out[j + 2] = d.orig[j + 2]! + (b - d.orig[j + 2]!) * blend;
      out[j + 3] = d.orig[j + 3]!;
    }
    d.mesh.updateVerticesData('color', out);
  };

  // ---- Wet track.
  const wetEntries = new Map<string, WetEntry>();
  const resolveWet = (): void => {
    for (const [name, style] of Object.entries(WET_STYLES)) {
      const mesh = scene.getMeshByName(name);
      const existing = wetEntries.get(name);
      if (existing && existing.mesh === mesh && !mesh.isDisposed()) continue;
      if (existing) {
        if (!existing.mesh.isDisposed() && existing.mesh.material === existing.wet) existing.mesh.material = existing.orig;
        existing.wet.dispose(false, false);
        wetEntries.delete(name);
      }
      if (!mesh || mesh.isDisposed() || !(mesh.material instanceof StandardMaterial)) continue;
      const orig = mesh.material;
      const { wet, rubber, fresnel } = makeWetTwin(orig, style);
      const entry: WetEntry = { mesh, orig, wet, style, rubber, fresnel, ready: false, active: false };
      wetEntries.set(name, entry);
      // Compile the wet shader now so the first shower doesn't hitch or blank the road.
      wet
        .forceCompilationAsync(mesh)
        .then(() => (entry.ready = true))
        .catch(() => undefined);
    }
  };
  resolveWet();
  let resolveTimer = 0;

  const sky = new Color3();
  const tmp = new Color3();
  const applyWet = (e: WetEntry, wetness: number, dryLine: number): void => {
    const { orig, wet, style } = e;
    // Something froze it (e.g. a scene-ready callback): it must stay live while wet.
    if (wet.isFrozen) wet.unfreeze();
    const k = 1 - wetness * (1 - style.darken);
    wet.diffuseColor.set(orig.diffuseColor.r * k, orig.diffuseColor.g * k, orig.diffuseColor.b * k);
    lerpColor(orig.specularColor, tmp.set(style.specular, style.specular, style.specular), wetness, wet.specularColor);
    wet.specularPower = lerp(orig.specularPower, style.specularPower, wetness);
    wet.alpha = orig.alpha;
    if (e.fresnel) {
      const W = style.sheen * wetness;
      let scale = W;
      if (e.rubber) {
        // emissive = (C + rubber) * scale: W off the line, W·(1−d) on it. The
        // rubber overlay also has a faint ~8 m band (0.94) around the line;
        // capping d at RUBBER_MAX_DRY keeps that band ≥ 90 % shiny.
        const d = Math.max(0.03, dryLine * RUBBER_MAX_DRY);
        scale = (W * d) / (1 - RUBBER_LINE);
        const c = (1 - RUBBER_LINE) / d - 1;
        wet.emissiveColor.set(c, c, c);
      } else {
        wet.emissiveColor.set(1, 1, 1);
      }
      // Grazing angles reflect the sky; looking straight down, nothing (rightColor stays black).
      e.fresnel.leftColor.set(sky.r * scale, sky.g * scale, sky.b * scale);
    }
  };

  const stats = { drops: 0, sprayParticles: 0, wetMaterials: 0 };
  let wasDry = false;

  const update = (s: WeatherState): void => {
    const rainNow = clamp01(finite(s.rain));
    const wetness = clamp01(finite(s.wetness));
    const dryLine = clamp01(finite(s.dryLine));
    const dt = Math.min(0.1, Math.max(0, finite(s.dt)));
    const speed = Math.max(0, finite(s.speed));
    heading = finite(s.heading);
    const q = opts.quality();

    resolveTimer += dt;
    if (resolveTimer > 1) {
      resolveTimer = 0;
      resolveRefs();
      resolveWet();
    }
    // Dry and already restored: nothing to do (and nothing touched, so other
    // code can change fog, lights or materials freely in the meantime).
    const dry = rainNow === 0 && wetness === 0;
    if (dry && wasDry) return;
    wasDry = dry;

    // Overcast amount: rain, lingering a little while the track is still wet.
    const oc = clamp01(Math.max(rainNow, 0.45 * wetness));
    // Even a drizzle comes with a full grey overcast; heavier rain darkens the palette.
    const blend = oc === 0 ? 0 : smooth(clamp01(oc * 2.2));
    lerpColor(OVERCAST.lightHorizon, OVERCAST.stormHorizon, rainNow, ocHorizon);
    lerpColor(OVERCAST.lightZenith, OVERCAST.stormZenith, rainNow, ocZenith);
    const fogK = clamp01(Math.max(rainNow, 0.15 * wetness));
    const fogT = fogK === 0 ? 0 : Math.pow(fogK, 0.8);

    sunIntensity.apply((b) => (blend === 0 ? b : b * (1 - 0.82 * blend)));
    hemiIntensity.apply((b) => (blend === 0 ? b : b * (1 + 0.12 * blend)));
    hemiDiffuse.apply((b, out) => lerpColor(b, OVERCAST.hemiDiffuse, blend, out));
    hemiGround.apply((b, out) => lerpColor(b, OVERCAST.hemiGround, blend, out));
    fogStart.apply((b) => (fogT === 0 ? b : lerp(b, Math.min(b, RAIN_FOG.start), fogT)));
    fogEnd.apply((b) => (fogT === 0 ? b : lerp(b, Math.min(b, RAIN_FOG.end), fogT)));
    // Haze and the clear colour match the dome's new horizon.
    fogColor.apply((b, out) => lerpColor(b, ocHorizon, blend, out));
    clearColor.apply((b, out) => lerpColor(b, ocHorizon, blend, out));
    exposure.apply((b) => (blend === 0 ? b : b - 0.12 * blend));
    contrast.apply((b) => (blend === 0 ? b : b - 0.06 * blend));
    const cm = cloudMat();
    if (cm && blend > 0 && cm.isFrozen) {
      cm.unfreeze();
      refreezeClouds = cm;
    }
    // Grey cloud masses a shade darker than the overcast behind them.
    cloudColor.apply((b, out) => lerpColor(b, tmp.copyFrom(ocHorizon).scaleInPlace(0.2), blend, out));
    cloudLevel.apply((b) => (blend === 0 ? b : lerp(b, ocHorizon.g * (0.8 - 0.12 * rainNow), blend)));
    cloudAlpha.apply((b) => (blend === 0 ? b : lerp(b, 0.85 * b, blend)));
    if (blend === 0 && refreezeClouds) {
      refreezeClouds.freeze();
      refreezeClouds = undefined;
    }
    const sunVis = clamp01(1 - blend * 1.6);
    sunDisc.apply((b) => (blend === 0 ? b : b * sunVis));
    const sunMesh = refs.sun;
    if (sunVis < 0.01 && sunMesh?.isEnabled()) {
      sunMesh.setEnabled(false);
      sunHidden = sunMesh;
    } else if (sunVis >= 0.01 && sunHidden) {
      sunHidden.setEnabled(true);
      sunHidden = undefined;
    }
    updateDome(blend, rainNow);

    // Wet track.
    // Sky colour seen in the sheen: the (overcast) horizon, a little brighter.
    sky.copyFrom(scene.fogColor).scaleInPlace(1.2);
    stats.wetMaterials = 0;
    for (const e of wetEntries.values()) {
      if (e.mesh.isDisposed()) continue;
      if (wetness > 0 && e.ready) {
        applyWet(e, wetness, dryLine);
        if (!e.active) {
          e.mesh.material = e.wet;
          e.active = true;
        }
        stats.wetMaterials++;
      } else if (e.active) {
        e.mesh.material = e.orig;
        e.active = false;
      }
    }

    // Rain streaks.
    const drops = rainNow > 0.005 ? Math.round(DROPS[q] * Math.pow(rainNow, 0.8)) : 0;
    stats.drops = drops;
    rain.setEnabled(drops > 0);
    if (drops > 0) {
      setQuadCount(rain, Math.min(maxDrops, drops));
      const vx = Math.sin(heading) * speed;
      const vz = Math.cos(heading) * speed;
      drift.x = (drift.x + WIND.x * dt) % BOX.x;
      drift.y = (drift.y - FALL_SPEED * dt) % BOX.y;
      drift.z = (drift.z + WIND.z * dt) % BOX.z;
      rainVel.set(WIND.x - vx, -FALL_SPEED, WIND.z - vz);
      rainAlpha = 0.5 * (0.6 + 0.4 * rainNow);
      // Drops pick up the light of the overcast sky.
      rainColor.copyFrom(ocHorizon).scaleInPlace(1.25);
    }

    // Spray: how much water the tyres can throw, and how fast.
    const water = wetness * (1 - 0.65 * dryLine) * (0.7 + 0.3 * rainNow);
    const speedK = smooth(clamp01((speed - 6) / 40));
    sprayAmount = water * speedK;
    const sprayN = sprayAmount > 0.01 ? spray.frontCount + SPRAY_REAR[q] * 2 : 0;
    stats.sprayParticles = sprayN;
    spray.mesh.setEnabled(sprayN > 0);
    if (sprayN > 0) {
      setQuadCount(spray.mesh, sprayN);
      sprayTime = (sprayTime + dt) % 600;
      spraySpeed = speed;
      spray.mesh.position.set(finite(s.carX), finite(s.carY), finite(s.carZ));
      spray.mesh.rotation.y = heading;
      sprayColor.copyFrom(ocHorizon).scaleInPlace(1.35);
    }
  };

  const dispose = (): void => {
    for (const k of numKnobs) k.restore();
    for (const k of colorKnobs) k.restore();
    if (sunHidden) sunHidden.setEnabled(true);
    sunHidden = undefined;
    if (refreezeClouds) refreezeClouds.freeze();
    refreezeClouds = undefined;
    if (dome && dome.blend !== 0 && !dome.mesh.isDisposed()) dome.mesh.updateVerticesData('color', dome.orig);
    dome = undefined;
    for (const e of wetEntries.values()) {
      if (!e.mesh.isDisposed() && e.mesh.material === e.wet) e.mesh.material = e.orig;
      e.wet.dispose(false, false);
    }
    wetEntries.clear();
    rain.dispose(false, true);
    spray.mesh.dispose(false, true);
  };
  return { update, dispose, stats };
}
